import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = "How to Diversify Missionary Support So One Donor or Church Can't Sink Your Budget";
const DESCRIPTION =
    "A practical guide to spreading missionary support across enough donors and churches that losing any single one is a dip, not a crisis — including a simple concentration test you can run today.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover31.jpg',
            datePublished: '2026-07-29',
            dateModified: '2026-07-29',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/diversify-missionary-support-sources',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'How much of my support is too much from one donor?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "A common rule of thumb is to keep any single donor, including your home church, under roughly 20-25% of your total monthly budget. Above that, losing them creates an emergency rather than a manageable gap.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Is it wrong to have my church as my biggest supporter?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "No — a strong home church commitment is healthy and often the foundation of a support base. The concern is concentration, not the relationship itself: the goal is growing everything else around it, not shrinking the church's role.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How many individual donors should a missionary aim for?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "There's no universal number, but many experienced fundraisers aim for 30-50+ individual givers so that no single relationship, life change, or lapse meaningfully threatens the budget.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How often should I re-check my support concentration?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Twice a year is a reasonable rhythm — once at your annual budget review and once mid-year — since concentration creeps up gradually as some donors increase gifts or others lapse, not all at once.",
                    },
                },
            ],
        },
    ],
};

export default function DiversifyMissionarySupportSources() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/diversify-missionary-support-sources' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-diversify-support-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover31.jpg" alt="A missionary reviewing a spreadsheet of donors and churches supporting their ministry" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Every missionary support base has a weak point, and it's usually invisible until the day it matters: one donor or church carries so much of the monthly total that losing them wouldn't just hurt — it would force an emergency response. Diversifying support isn't about distrusting your biggest givers; it's about making sure no single relationship, however solid it feels today, can single-handedly threaten your ability to stay on the field.</p>

                    <h2>Run the concentration test</h2>

                    <p>List every recurring donor and church against your total monthly support, and calculate what percentage each one represents. Anything above roughly 20-25% from a single source is worth flagging — not necessarily fixing today, but tracking. See <a href="/resources/what-to-do-when-a-major-donor-stops-giving">what to do when a major donor stops giving</a> for what the recovery process looks like if a concentrated donor does lapse; the goal of this exercise is to make that scenario rare rather than routine.</p>

                    <h2>Why concentration happens even when you're not trying to create it</h2>

                    <p>Nobody sets out to build a lopsided support base. It usually happens gradually: your home church commits early and generously because the relationship is closest, a handful of individual gifts grow over the years while newer relationships stay flat, or a single major donor steps up during a shortfall and their gift becomes the new normal. None of this is a mistake — it's just support raising happening in the order relationships naturally develop, without anyone tracking the resulting shape.</p>

                    <h2>Growing the base without diluting your best relationships</h2>

                    <p>The fix for concentration is almost never to ask your biggest supporters to give less — it's to grow everything else around them. Three practical moves: widen the ask beyond your closest circle using the approach in <a href="/resources/raising-support-with-no-network">raising support with no network</a>, convert one-time and mission-trip givers into recurring supporters (see <a href="/resources/how-to-ask-a-one-time-donor-to-become-a-monthly-supporter">how to ask a one-time donor to become a monthly supporter</a>), and treat every new relationship — a small group leader, a former coworker, a distant relative — as a future recurring line rather than a one-off.</p>

                    <h2>A second church or partnering ministry, not a replacement</h2>

                    <p>If one congregation makes up a large share of your budget, a second sending church or partnering ministry doesn't compete with the first — it protects it. Approach a second church the same way you approached your first: see <a href="/resources/how-to-ask-your-church-to-support-you-as-a-missionary">how to ask your church to support you as a missionary</a> for the process, and be transparent that you're building a broader base, not shopping for a bigger check.</p>

                    <h2>What a healthy spread looks like</h2>

                    <p>There's no single correct ratio, but a support base where your largest single source is under a quarter of the total, and where 30 or more distinct donors together make up the rest, tends to absorb a lapse without forcing a scramble. The exact numbers matter less than the habit of checking: a base that was well-diversified two years ago can quietly re-concentrate as some donors grow and others fade, so revisit the test on a regular schedule rather than once.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>How much of my support is too much from one donor?</strong><br />
                    A common guideline is keeping any single source under roughly 20-25% of your total.</p>

                    <p><strong>Is it wrong to have my church as my biggest supporter?</strong><br />
                    No — the goal is growing everything else around that relationship, not shrinking it.</p>

                    <p><strong>How many individual donors should a missionary aim for?</strong><br />
                    Many experienced fundraisers aim for 30-50+ so no single relationship can threaten the budget.</p>

                    <p><strong>How often should I re-check my support concentration?</strong><br />
                    Roughly twice a year, since concentration creeps up gradually rather than all at once.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/what-to-do-when-a-major-donor-stops-giving">What to Do When a Major Donor Stops Giving</a></li>
                        <li><a href="/resources/raising-support-with-no-network">How to Raise Missionary Support When You Don't Have a Big Network</a></li>
                        <li><a href="/resources/how-to-ask-a-one-time-donor-to-become-a-monthly-supporter">How to Ask a One-Time Donor to Become a Monthly Supporter</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We built this after watching missionaries get blindsided by the loss of a single major donor they never realized was carrying a quarter of their budget.</p>
                    <p><small><em>Last updated: 2026-07-29.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/diversify-missionary-support-sources" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
